import React, { useEffect, useMemo, useState } from "react";
import { Settings, History, Users, Zap, Clock, Filter, RefreshCw, Loader } from "lucide-react";
import ReusableDropdown from "../../../components/ReusableDropdown";
import SearchBar from "../../../components/SearchBar";
import EmployeeAvatarCell from "../../../components/EmployeeAvatarCell";
import MobileFilterToggle from "../../../components/MobileFilterToggle";
import Pagination from "../../../components/Pagination";
import { getCardStyle, getThHeaderStyle, getTdStyle, getTopBarButtonStyle } from "../../(Admin)Attendance/styles/attendanceStyles";

const PAGE_SIZE = 10;

const getScreenSize = () => {
  if (typeof window === "undefined") return "lg";
  const w = window.innerWidth;
  if (w < 640) return "xs";
  if (w < 768) return "sm";
  if (w < 1024) return "md";
  return "lg";
};

// Same colours as the Employees page status badge.
const STATUS_TONES = {
  active: { color: "#10b981", ar: "نشط", en: "Active" },
  onleave: { color: "#f59e0b", ar: "في إجازة", en: "On Leave" },
  inactive: { color: "#ef4444", ar: "غير نشط", en: "Inactive" },
  suspended: { color: "#f97316", ar: "موقوف", en: "Suspended" },
};

/**
 * Filters + employee table of the Changes page, laid out like the Employees
 * page: a compact "Filters & Actions" card (one row of fields on desktop)
 * and a paginated table card using the shared Attendance table styles.
 */
const EmployeeTable = ({
  t,
  theme,
  isRTL,
  language,
  isLoading,
  employees,
  filteredEmployees,
  companies,
  departments,
  statuses,
  searchTerm,
  setSearchTerm,
  filterCompany,
  setFilterCompany,
  filterDepartment,
  setFilterDepartment,
  filterStatus,
  setFilterStatus,
  getEmployeeName,
  hasFeatureAccess,
  isColumnVisible,
  canViewPendingApprovals,
  pendingActions,
  onOpenHistory,
  onOpenPending,
  onAction,
  onHistory,
  onClearFilters,
}) => {
  const dark = theme === "dark";
  const lang = language === "ar" ? "ar" : "en";
  const [screenSize, setScreenSize] = useState(getScreenSize);
  const [currentPage, setCurrentPage] = useState(1);
  // Phones: company/department/status fold behind a "Filters" button.
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    const onResize = () => setScreenSize(getScreenSize());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterCompany, filterDepartment, filterStatus]);

  const canViewHistory = !!hasFeatureAccess && hasFeatureAccess("changesPage", "viewhistory");
  const canPerformAction = !hasFeatureAccess || hasFeatureAccess("changesPage", "performaction");
  const showCompanyFilter = (companies || []).length > 1;

  const columns = useMemo(() => {
    const all = [
      { key: "employee", label: t.employee, align: "start" },
      { key: "code", label: t.code || (lang === "ar" ? "الكود" : "Code") },
      { key: "department", label: t.department },
      { key: "position", label: t.position },
      { key: "reportsTo", label: t.directManager || (lang === "ar" ? "المدير المباشر" : "Direct Manager") },
      { key: "status", label: t.status },
      { key: "actions", label: t.actions },
    ];
    // Admin-level hidden columns (opt-out: unknown keys stay visible).
    return all.filter((c) => !isColumnVisible || isColumnVisible("changesPage", "employeeActionsTable", c.key));
  }, [t, lang, isColumnVisible]);
  const has = (key) => columns.some((c) => c.key === key);

  // infinityCode -> number of the employee's requests still waiting approval.
  const pendingByEmployee = useMemo(() => {
    const map = {};
    (pendingActions || []).forEach((a) => {
      const code = a?.infinityCode || a?.employee?.infinityCode;
      if (code) map[code] = (map[code] || 0) + 1;
    });
    return map;
  }, [pendingActions]);

  const activeFilterCount = [
    searchTerm,
    filterCompany !== "all" && filterCompany,
    filterDepartment !== "all" && filterDepartment,
    filterStatus !== "all" && filterStatus,
  ].filter(Boolean).length;

  const totalPages = Math.max(1, Math.ceil(filteredEmployees.length / PAGE_SIZE));
  const page = Math.min(currentPage, totalPages);
  const pageRows = filteredEmployees.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const isPhone = screenSize !== "lg";

  // ── Tokens ────────────────────────────────────────────────────────────────
  const muted = dark ? "rgba(255,255,255,0.6)" : "rgba(26,26,26,0.6)";
  const textPrimary = dark ? "#ffffff" : "#1a1a1a";
  const divider = dark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(15,23,42,0.07)";
  const th = getThHeaderStyle(theme);
  const td = getTdStyle(theme);

  // Row buttons: same neutral icon buttons as the Employees page table
  // (EmployeeTableIntegrated btnStyle "neutral"), icon + label.
  const rowButton = {
    padding: "8px 12px",
    borderRadius: "8px",
    border: "none",
    background: dark ? "rgba(71,85,105,0.6)" : "rgba(226,232,240,0.8)",
    color: dark ? "#f1f5f9" : "#334155",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "12px",
    fontWeight: 600,
    fontFamily: "inherit",
    whiteSpace: "nowrap",
    transition: "all 0.2s ease",
  };

  // Same label/field layout as the Employees page FiltersPanel.
  const labelStyle = {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    minHeight: "28px",
    marginBottom: "8px",
    fontSize: "14px",
    fontWeight: 600,
    color: textPrimary,
  };
  const fieldStyle = { display: "flex", flexDirection: "column", minWidth: 0 };

  const statusBadge = (state) => {
    if (!state) return <span style={{ color: muted }}>—</span>;
    const tone = STATUS_TONES[String(state).replace(/[\s_-]/g, "").toLowerCase()];
    const color = tone?.color || "#64748b";
    return (
      <span
        style={{
          display: "inline-block",
          padding: "4px 12px",
          borderRadius: "20px",
          fontSize: "12px",
          fontWeight: 600,
          whiteSpace: "nowrap",
          color,
          background: `${color}${dark ? "33" : "1a"}`,
          border: `1px solid ${color}`,
        }}
      >
        {tone ? tone[lang] : state}
      </span>
    );
  };

  const pendingBadge = (emp) => {
    const n = pendingByEmployee[emp.infinityCode];
    if (!n) return null;
    return (
      <span
        title={t.pendingApprovals}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
          padding: "2px 8px",
          borderRadius: "20px",
          fontSize: "11px",
          fontWeight: 700,
          whiteSpace: "nowrap",
          color: dark ? "#fbbf24" : "#b45309",
          background: dark ? "rgba(245,158,11,0.18)" : "rgba(245,158,11,0.12)",
        }}
      >
        <Clock size={11} />
        {n > 1 ? `${n} ` : ""}
        {t.pending}
      </span>
    );
  };

  const deptOf = (emp) => emp.department?.name ?? emp.employeeType ?? "—";
  const positionOf = (emp) => emp.position || emp.employeeType || "—";
  const codeOf = (emp) => emp.clientCode || emp.infinityCode || "—";

  const rowActions = (emp) => (
    <>
      {canPerformAction && (
        <button
          type="button"
          className="nx-ea-rowbtn"
          style={rowButton}
          onClick={() => onAction(emp)}
        >
          <Zap size={14} />
          {t.employeeAction}
        </button>
      )}
      {canViewHistory && (
        <button
          type="button"
          className="nx-ea-rowbtn"
          style={rowButton}
          onClick={() => onHistory(emp)}
        >
          <History size={14} />
          {t.employeeHistory}
        </button>
      )}
    </>
  );

  const pendingCount = (pendingActions || []).length;

  return (
    <>
      <style>{`
        .nx-ea-rowbtn:hover { background: ${dark ? "rgba(71,85,105,0.8)" : "rgba(203,213,225,0.8)"} !important; transform: translateY(-2px); }
        .nx-ea-topbtn:not(:disabled):hover { background: ${dark ? "rgba(30, 41, 59, 1)" : "rgba(51, 65, 85, 0.2)"} !important; }
        .nx-ea-topbtn--primary:hover { background: #276aa0 !important; }
        .nx-ea-row:hover td { background: ${dark ? "rgba(59,130,246,0.08)" : "rgba(59,130,246,0.04)"}; }
      `}</style>

      {/* ── Filters & Actions (same layout as the Employees page) ────────── */}
      <div style={{ ...getCardStyle(theme), marginTop: 0, marginBottom: "24px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
            flexWrap: "wrap",
            gap: "16px",
          }}
        >
          <h3 style={{ margin: 0, fontSize: "clamp(16px, 3vw, 20px)", fontWeight: 700, display: "flex", alignItems: "center", gap: "10px", color: textPrimary }}>
            <Settings size={20} />
            {t.filtersActions || (lang === "ar" ? "الفلاتر والإجراءات" : "Filters & Actions")}
          </h3>
          <MobileFilterToggle
            open={filtersOpen}
            onToggle={() => setFiltersOpen((o) => !o)}
            activeCount={[filterCompany, filterDepartment, filterStatus].filter((v) => v && v !== "all").length}
            language={language}
            theme={theme}
          />
        </div>

        <div
          className="nx-filter-grid"
          data-open={String(filtersOpen)}
          style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: "20px" }}
        >
          <div style={fieldStyle}>
            <label style={labelStyle}>{t.searchEmployee}</label>
            <div style={{ marginTop: "auto" }}>
              <SearchBar
                value={searchTerm}
                onChange={setSearchTerm}
                placeholder={t.searchPlaceholder}
                theme={theme}
                language={language}
                icon={true}
              />
            </div>
          </div>

          <div className="nx-filter-extra" style={fieldStyle}>
            <label style={labelStyle}>
              <Filter size={16} />
              {t.department}
            </label>
            <div style={{ marginTop: "auto", position: "relative" }}>
              <ReusableDropdown
                items={[{ id: "all", name: t.allDepartments }, ...departments.map((d) => ({ id: d, name: d }))]}
                value={filterDepartment === "all" ? "" : filterDepartment}
                onChange={(val) => setFilterDepartment(val || "all")}
                onClear={() => setFilterDepartment("all")}
                getItemKey={(it) => it.id}
                getItemLabel={(it) => it.name}
                placeholder={lang === "ar" ? "ابحث عن قسم" : "Search Department"}
                theme={theme}
                language={language}
                maxItems={8}
                showAllOnArrowClick={true}
              />
            </div>
          </div>

          <div className="nx-filter-extra" style={fieldStyle}>
            <label style={labelStyle}>
              <Filter size={16} />
              {t.status}
            </label>
            <div style={{ marginTop: "auto", position: "relative" }}>
              <ReusableDropdown
                items={[
                  { id: "all", name: t.allStatuses },
                  ...statuses.map((st) => {
                    const tone = STATUS_TONES[String(st).replace(/[\s_-]/g, "").toLowerCase()];
                    return { id: st, name: tone ? tone[lang] : st };
                  }),
                ]}
                value={filterStatus === "all" ? "" : filterStatus}
                onChange={(val) => setFilterStatus(val || "all")}
                onClear={() => setFilterStatus("all")}
                getItemKey={(it) => it.id}
                getItemLabel={(it) => it.name}
                placeholder={lang === "ar" ? "اختر الحالة" : "Select Status"}
                theme={theme}
                language={language}
                maxItems={6}
                showAllOnArrowClick={true}
              />
            </div>
          </div>

          {showCompanyFilter && (
            <div className="nx-filter-extra" style={fieldStyle}>
              <label style={labelStyle}>
                <Filter size={16} />
                {t.company}
              </label>
              <div style={{ marginTop: "auto", position: "relative" }}>
                <ReusableDropdown
                  items={[{ id: "all", name: t.allCompanies }, ...companies.map((c) => ({ id: c, name: c }))]}
                  value={filterCompany === "all" ? "" : filterCompany}
                  onChange={(val) => setFilterCompany(val || "all")}
                  onClear={() => setFilterCompany("all")}
                  getItemKey={(it) => it.id}
                  getItemLabel={(it) => it.name}
                  placeholder={t.allCompanies}
                  theme={theme}
                  language={language}
                  maxItems={8}
                  showAllOnArrowClick={true}
                />
              </div>
            </div>
          )}
        </div>

        {(canViewHistory || (canViewPendingApprovals && onOpenPending)) && (
          <div
            className="nx-actions-row"
            style={{
              display: "flex",
              gap: "10px",
              flexWrap: "wrap",
              marginTop: "20px",
              paddingTop: "16px",
              borderTop: dark ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.1)",
            }}
          >
            {canViewHistory && (
              <button
                type="button"
                className="nx-ea-topbtn--primary"
                style={{ ...getTopBarButtonStyle(theme, false), background: "#2e7cba", color: "#fff", border: "none" }}
                onClick={onOpenHistory}
              >
                <History size={16} />
                {t.viewHistory}
              </button>
            )}
            {canViewPendingApprovals && onOpenPending && (
              <button type="button" className="nx-ea-topbtn" style={getTopBarButtonStyle(theme, false)} onClick={onOpenPending}>
                <Clock size={16} />
                {t.pendingApprovals}
                {pendingCount > 0 && (
                  <span
                    style={{
                      minWidth: "20px",
                      height: "20px",
                      padding: "0 6px",
                      borderRadius: "10px",
                      background: "#f59e0b",
                      color: "#fff",
                      fontSize: "11px",
                      fontWeight: 700,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {pendingCount}
                  </span>
                )}
              </button>
            )}
          </div>
        )}
      </div>

      {/* ── Employees table ───────────────────────────────────────────── */}
      <div style={{ ...getCardStyle(theme), marginTop: 0, marginBottom: "24px", position: "relative" }}>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: "8px", marginBottom: "16px" }}>
          <h3 style={{ margin: 0, fontSize: "18px", fontWeight: 700, display: "flex", alignItems: "center", gap: "8px", color: textPrimary }}>
            <Users size={20} />
            {t.employees || (lang === "ar" ? "الموظفين" : "Employees")}
            <span style={{ fontSize: "14px", fontWeight: 600, color: muted }}>({filteredEmployees.length})</span>
          </h3>
          {filteredEmployees.length !== employees.length && (
            <span style={{ fontSize: "13px", color: muted, fontWeight: 500 }}>
              {t.showing} {filteredEmployees.length} {t.of} {employees.length}
            </span>
          )}
        </div>

        {isLoading && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: "1rem",
              background: dark ? "rgba(15,23,42,0.6)" : "rgba(255,255,255,0.7)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 20,
            }}
          >
            <Loader size={32} color="#2e7cba" style={{ animation: "spin 1s linear infinite" }} />
          </div>
        )}

        {pageRows.length === 0 ? (
          <div style={{ textAlign: "center", padding: "48px 24px", color: muted }}>
            <Users size={40} style={{ opacity: 0.5, marginBottom: "12px" }} />
            <p style={{ margin: "0 0 4px", fontSize: "16px", fontWeight: 600, color: textPrimary }}>
              {isLoading ? t.loadingEmployees : t.noEmployeesFound}
            </p>
            {!isLoading && <p style={{ margin: 0, fontSize: "14px" }}>{t.noEmployeesMatch}</p>}
            {!isLoading && activeFilterCount > 0 && (
              <button type="button" className="nx-ea-topbtn" style={{ ...getTopBarButtonStyle(theme, false), display: "inline-flex", marginTop: "16px" }} onClick={onClearFilters}>
                <RefreshCw size={15} /> {t.clearFilters}
              </button>
            )}
          </div>
        ) : isPhone ? (
          <div
            className="nx-mcard-list"
            style={{
              display: "grid",
              gridTemplateColumns: screenSize === "md" ? "repeat(2, minmax(0, 1fr))" : "1fr",
              gap: "12px",
              alignItems: "start",
            }}
          >
            {pageRows.map((emp) => {
              const meta = [
                has("code") && codeOf(emp),
                has("department") && deptOf(emp),
                has("position") && positionOf(emp),
              ].filter((v) => v && v !== "—");
              return (
                <article
                  key={emp.id || emp.infinityCode}
                  style={{
                    padding: "12px 14px",
                    borderRadius: "14px",
                    background: dark ? "rgba(255,255,255,0.03)" : "#ffffff",
                    border: dark ? "1px solid rgba(255,255,255,0.09)" : "1px solid rgba(15,23,42,0.08)",
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "10px", minWidth: 0 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <EmployeeAvatarCell employee={emp} language={language} theme={theme} getEmployeeName={getEmployeeName} />
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "4px" }}>
                      {has("status") && statusBadge(emp.employeeState)}
                      {pendingBadge(emp)}
                    </div>
                  </div>
                  {meta.length > 0 && <div style={{ fontSize: "12.5px", color: muted }}>{meta.join(" · ")}</div>}
                  {has("reportsTo") && emp.firstLineManagerName && (
                    <div style={{ fontSize: "12.5px", color: muted }}>
                      {t.directManager || (lang === "ar" ? "المدير المباشر" : "Direct Manager")}: {emp.firstLineManagerName}
                    </div>
                  )}
                  {has("actions") && (canPerformAction || canViewHistory) && (
                    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", paddingTop: "10px", borderTop: divider }}>
                      {rowActions(emp)}
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        ) : (
          <div style={{ overflowX: "auto", borderRadius: "12px" }} className="scrollbar-hidden">
            <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, minWidth: "900px" }}>
              <thead>
                <tr>
                  {columns.map((c) => (
                    <th key={c.key} style={{ ...th, textAlign: c.align || "center" }}>
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pageRows.map((emp) => (
                  <tr key={emp.id || emp.infinityCode} className="nx-ea-row">
                    {has("employee") && (
                      <td style={{ ...td, textAlign: "start", padding: "12px 16px" }}>
                        <EmployeeAvatarCell employee={emp} language={language} theme={theme} getEmployeeName={getEmployeeName} />
                      </td>
                    )}
                    {has("code") && (
                      <td style={{ ...td, fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>{codeOf(emp)}</td>
                    )}
                    {has("department") && <td style={td}>{deptOf(emp)}</td>}
                    {has("position") && <td style={td}>{positionOf(emp)}</td>}
                    {has("reportsTo") && <td style={td}>{emp.firstLineManagerName || "—"}</td>}
                    {has("status") && (
                      <td style={td}>
                        <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: "4px" }}>
                          {statusBadge(emp.employeeState)}
                          {pendingBadge(emp)}
                        </div>
                      </td>
                    )}
                    {has("actions") && (
                      <td style={td}>
                        <div style={{ display: "flex", gap: "6px", alignItems: "center", justifyContent: "center" }}>
                          {rowActions(emp)}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            theme={theme}
            isRTL={isRTL}
          />
        )}
      </div>
    </>
  );
};

export default EmployeeTable;
